import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { FirebaseError } from 'firebase/app';
import { claimDanceOffPrize } from '../../services/danceOff';
import { getAttendeeIdAsync, getAttendeeNickname } from '../../services/attendeeIdentity';
import { isTestIdentityEnabled, signInAsTestUser } from '../../services/testIdentity';

type ClaimState = { kind: 'claiming' } | { kind: 'claimed' } | { kind: 'error'; message: string };

const INVALID_CODE_MESSAGE = "This claim code isn't valid.";

function claimErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'functions/already-exists':
        return 'This prize has already been claimed.';
      case 'functions/permission-denied':
      case 'functions/not-found':
        return INVALID_CODE_MESSAGE;
      case 'functions/failed-precondition':
        return error.message;
    }
  }
  return "Couldn't claim the prize — ask the DJ.";
}

async function resolveAttendeeId(testUserParam: string | null): Promise<string> {
  const testSlot = testUserParam ? parseInt(testUserParam, 10) : NaN;
  if (isTestIdentityEnabled() && testSlot >= 1 && testSlot <= 10) {
    return signInAsTestUser(testSlot);
  }
  return getAttendeeIdAsync();
}

export default function DanceOffClaimPage() {
  const { eventId, claimId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<ClaimState>({ kind: 'claiming' });
  const startedRef = useRef(false);

  const params = new URLSearchParams(location.search);
  const testUserParam = params.get('testUser');
  const eventPath = `/e/${eventId}${testUserParam ? `?testUser=${encodeURIComponent(testUserParam)}` : ''}`;

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = new URLSearchParams(location.search).get('t');
    const keptSearch = testUserParam ? `?testUser=${encodeURIComponent(testUserParam)}` : '';
    navigate(`${location.pathname}${keptSearch}`, { replace: true });

    if (!claimId || !token) {
      setState({ kind: 'error', message: INVALID_CODE_MESSAGE });
      return;
    }

    let redirectTimer: ReturnType<typeof setTimeout> | null = null;
    const claim = async () => {
      try {
        await resolveAttendeeId(testUserParam);
        const nickname = getAttendeeNickname() || undefined;
        await claimDanceOffPrize({ claimId, token, nickname });
        setState({ kind: 'claimed' });
        redirectTimer = setTimeout(() => navigate(eventPath, { replace: true }), 1500);
      } catch (error) {
        console.error('[DanceOffClaim] Claim failed:', error);
        setState({ kind: 'error', message: claimErrorMessage(error) });
      }
    };
    void claim();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-sm w-full text-center space-y-4">
        {state.kind === 'claiming' && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
            <p className="text-gray-700 dark:text-gray-300">Claiming your prize…</p>
          </>
        )}
        {state.kind === 'claimed' && (
          <>
            <p className="text-5xl">🎉</p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Prize claimed!</h1>
            <p className="text-gray-600 dark:text-gray-400">Taking you to the event…</p>
          </>
        )}
        {state.kind === 'error' && (
          <>
            <p className="text-5xl">💃</p>
            <h1 role="alert" className="text-xl font-semibold text-gray-900 dark:text-white">{state.message}</h1>
            <Link
              to={eventPath}
              className="inline-block px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90"
            >
              Go to the event
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
